import { loadComponent } from "../components/ComponentsLoader";
import { fetchMixinItem } from "./Mixin";
import { loadWidget } from "./Widget";
import { loadWidgetContainer } from "./WidgetContainer";
import promiseAllSettled from "../utils/promiseAllSettled";
import webui from "../app/InternalStateService";

const ITEMS = "items";
const ITEM = "item";
const CHILDREN = "children";
const COMPONENTS = "components";
const MIXIN = "mixin";
const HEADER = "header";
const BODY = "body";
const FOOTER = "footer";
const ROW_DETAILS = "rowDetails";

const loadInnerComponent = function(children, mixinQueue, requestPromises, widgetId) {
    children.forEach(item => {
        let innerComponent = loadMissingComponents({ config: item, mixinQueue, widgetId });
        requestPromises.push(innerComponent);
    });
};
//loads missing components and mixins recursively
//returns updated config, when all components are loaded
export const loadMissingComponents = async ({
    config = {}, //current config item
    mixinQueue = [], //mixin queue for avoiding issue with nested mixin loops
    widgetId = "" //current widget id, for context specific loading
} = {}) => {
    let children = []; //children to be loaded
    let requestPromises = []; // returns an array of all promises to be executed
    //load mixin
    if (config.type === MIXIN) {
        let newMixinQueue = [...mixinQueue];
        await fetchMixinItem(config, newMixinQueue, widgetId);
        //loading components from mixin result
        return loadMissingComponents({
            config,
            mixinQueue: newMixinQueue,
            widgetId
        });
    }
    // load component
    if (config.type) {
        //make a search in the current context and the global context as well
        if (!webui.hasComponent(config.type, { widgetId })) {
            requestPromises.push(loadComponent(config.type));
        }
    }

    //load children components
    children = config[COMPONENTS] || config[CHILDREN] || config[ITEMS];
    if (!children && config[ITEM]) {
        children = [config[ITEM]];
    }
    if (children && children.length) {
        loadInnerComponent(children, mixinQueue, requestPromises, widgetId);
    }
    //load table header components
    if (config[HEADER]) {
        loadInnerComponent([config[HEADER]], mixinQueue, requestPromises, widgetId);
    }
    //load table body components
    if (config[BODY]) {
        loadInnerComponent([config[BODY]], mixinQueue, requestPromises, widgetId);
    }
    //load table footer components
    if (config[FOOTER]) {
        loadInnerComponent([config[FOOTER]], mixinQueue, requestPromises, widgetId);
    }
    //load table expandable row components
    if (config[ROW_DETAILS]) {
        loadInnerComponent([config[ROW_DETAILS]], mixinQueue, requestPromises, widgetId);
    }

    // waiting for all inner components to be loaded before completing
    await Promise.all(requestPromises);
    //returning updated config(updated while loading mixins)
    return config;
};

//traverses SIREN data and loads inner widgets recursively, checks for property "type" === "widget"
//loads data if  "active" === true (default)
//TODO cache logic
export const loadInnerWidgets = async (
    dispatch,
    parentWidget,
    data = {},
    options = {},
    //holds promises for loading all inner widgets in data tree
    requestPromises = []
) => {
    let properties = data.properties || {};
    let active = properties.hasOwnProperty("active") ? properties.active : true;
    //type widget as ui component describes a single widget
    let widget = properties.type === "widget";
    //type widgetContainer as ui component describes a defined template with widgets in its regions
    let widgetContainer = properties.type === "widgetContainer";

    if ((widget || widgetContainer) && active) {
        let widgetPathId = properties.id || properties.pathId || data.class[0];
        // let widgetPath = parentWidget + "." + widgetPathId;
        let widgetName = properties.name || widgetPathId;

        let context = properties.context;
        if (!context && parentWidget) {
            let directParent = parentWidget.split(".");
            context = webui.getWidgetContext(directParent[directParent.length - 1]);
        }

        let loadAction = loadWidget;
        if (widgetContainer) {
            loadAction = loadWidgetContainer;
        }

        let widgetPromise = dispatch(
            //expected properties - cached, id, href, parentWidget, method, sources, name, parameters
            //to remove skipLoading ??? second argument
            loadAction(
                {
                    id: widgetName,
                    parentWidget,
                    cached: properties.cached || [],
                    href: properties.href || [],
                    //POST/GET default: GET
                    method: properties.method || "GET",
                    sources: properties.sources,
                    //to be used for caching purposes
                    name: widgetName,
                    context
                },
                false,
                properties.parameters
            )
        );
        requestPromises.push(widgetPromise);
    }
    if (data.hasOwnProperty("entities") && options.recursive !== false) {
        for (let i = 0; data.entities.length > i; i++) {
            loadInnerWidgets(dispatch, parentWidget, data.entities[i], options, requestPromises);
        }
    }
    await promiseAllSettled(requestPromises);
    return data;
};
